import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCycle } from '../context/CycleContext';
import CycleSelector from './CycleSelector';
import './Layout.css';

// Paths that require an active paid cycle
const JOURNAL_PATHS = [
    '/journal/self-assessment',
    '/journal/current-health',
    '/journal/reflection',
    '/journal/transformation',
    '/journal/progress',
    '/journal/checkin-01',
    '/journal/checkin-02',
    '/journal/checkin-03',
    '/journal/checkin-04',
    '/journal/checkin-05',
    '/journal/checkin-06',
];

const Layout = () => {
    const { user, logout } = useAuth();
    const { currentCycle, loading, paymentRequired } = useCycle();
    const location = useLocation();
    const navigate = useNavigate();
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    // Sections are locked when user has no cycle (brand-new, hasn't paid)
    const sectionsLocked = !loading && paymentRequired && !currentCycle;

    const navigation = [
        { name: 'Dashboard', path: '/dashboard', icon: '📊', locked: false },
        { name: 'Self Assessment', path: '/journal/self-assessment', icon: '📝', locked: true },
        { name: 'Reflection', path: '/journal/reflection', icon: '🤔', locked: true },
        { name: 'Transformation', path: '/journal/transformation', icon: '🌱', locked: true },
        { name: 'Evaluation: Check-in #01', path: '/journal/checkin-01', icon: '✅', locked: true },
        { name: 'Evaluation: Check-in #02', path: '/journal/checkin-02', icon: '✅', locked: true },
        { name: 'Evaluation: Check-in #03', path: '/journal/checkin-03', icon: '✅', locked: true },
        { name: 'Evaluation: Check-in #04', path: '/journal/checkin-04', icon: '✅', locked: true },
        { name: 'Evaluation: Check-in #05', path: '/journal/checkin-05', icon: '✅', locked: true },
        { name: 'Evaluation: Check-in #06', path: '/journal/checkin-06', icon: '✅', locked: true },
        { name: 'Settings', path: '/settings', icon: '⚙️', locked: false }
    ];

    const handleLogout = async () => {
        await logout();
    };

    const handleNavClick = (item, e) => {
        setShowMobileMenu(false);
        // Block navigation to journal sections if locked
        if (sectionsLocked && item.locked) {
            e.preventDefault();
            navigate('/dashboard');
        }
    };

    // If user is on a journal page but sections are locked, show gate
    const isOnLockedPage = sectionsLocked && JOURNAL_PATHS.some(p => location.pathname.startsWith(p));

    return (
        <div className="layout">
            <header className="header">
                <div className="header-content">
                    <div className="header-left">
                        <button
                            className="mobile-menu-btn"
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                        >
                            ☰
                        </button>
                        <h1 className="logo">Dr. Within Health Journal</h1>
                    </div>

                    <div className="header-right">
                        <span className="user-name">{user?.fullName}</span>
                        <button onClick={handleLogout} className="btn btn-secondary btn-sm">
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="layout-body">
                <aside className={`sidebar ${showMobileMenu ? 'show' : ''}`}>
                    <CycleSelector />
                    <nav className="nav">
                        {navigation.map((item) => {
                            const isLocked = sectionsLocked && item.locked;
                            return (
                                <Link
                                    key={item.path}
                                    to={isLocked ? '#' : item.path}
                                    className={`nav-item ${location.pathname === item.path ? 'active' : ''} ${isLocked ? 'nav-locked' : ''}`}
                                    onClick={(e) => handleNavClick(item, e)}
                                >
                                    <span className="nav-icon">{isLocked ? '🔒' : item.icon}</span>
                                    <span className="nav-label">{item.name}</span>
                                </Link>
                            );
                        })}
                    </nav>
                </aside>

                <main className="main-content">
                    {isOnLockedPage ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            minHeight: '60vh', textAlign: 'center', padding: '2rem'
                        }}>
                            <div>
                                <h2 style={{ color: '#333', marginBottom: '1rem' }}>🔒 Journal Locked</h2>
                                <p style={{ color: '#666', maxWidth: '400px' }}>
                                    Complete your payment to unlock all journal sections.
                                    Use the payment option in the sidebar to get started.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <Outlet />
                    )}
                </main>
            </div>
        </div>
    );
};

export default Layout;
